import { useState } from 'react';
import { message } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './redux';
import { createUserShip, fetchUserShip, updateShippingAddress } from '../services';
import { resetLocation } from '../redux/slices/locationSlice';
import { setGuestShippingAddress } from '../redux/slices/shoppingCartSlice';
import {
  IshipAddres,
  ShippingFieldErrors,
  ShippingUpdateOptions,
} from '../components/interfaces';
import {
  buildCreateShippingAddressData,
  buildUpdatedShippingData,
  getAddressVerificationErrorFields,
  getRequiredShippingFieldErrors,
  hasShippingFieldErrors,
  isGuestCheckoutRoute,
  isValidGuestEmail,
} from './shippingUpdate.helpers';
import { verifyShippingAddress } from './verifyShippingAddress';

const isPassiveAddressVerificationWarning = (warning?: string) =>
  String(warning || '').toLowerCase().includes('verification is temporarily unavailable');

export const useShippingUpdate = (
  userInfo: IshipAddres,
  setErrors: React.Dispatch<React.SetStateAction<ShippingFieldErrors>>,
  isCreatingNewAddress: boolean
) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = useAppSelector((state) => state.user.user?.id);
  const guestEmail = useAppSelector((state) => state.shoppingCart.guestEmail);
  const { shipping, selectedAddress } = useAppSelector((state) => state.user || {});
  const [loading, setLoading] = useState(false);
  const [updateButtonClicked, setUpdateButtonClicked] = useState(false);
  const [addressValidationMessage, setAddressValidationMessage] = useState<string | null>(null);

  const handleUpdateProfile = async (options?: ShippingUpdateOptions) => {
    if (loading) return false;

    setUpdateButtonClicked(true);
    setLoading(true);
    const shouldNavigate = options?.navigateOnSuccess ?? true;

    try {
      const updatedData = buildUpdatedShippingData(userInfo, shipping);
      const createAddressData = buildCreateShippingAddressData(updatedData);
      const fieldErrors = getRequiredShippingFieldErrors(updatedData);

      if (hasShippingFieldErrors(fieldErrors)) {
        setErrors(fieldErrors);
        setAddressValidationMessage('Please fill in all required fields.');
        message.error('Please fill in all required fields.');
        return false;
      }

      if (!isGuestCheckoutRoute(userId, location.pathname)) {
        const addressValidation = await verifyShippingAddress(updatedData);

        if (!addressValidation.ok) {
          setErrors((prev) => ({
            ...prev,
            ...getAddressVerificationErrorFields(),
          }));
          setAddressValidationMessage('The address looks incorrect. Please check street, city, state, postcode, and country.');
          return false;
        }

        if (addressValidation.warning && !isPassiveAddressVerificationWarning(addressValidation.warning)) {
          message.warning(`Address saved, but please review it: ${addressValidation.warning}`);
        }
      }

      setAddressValidationMessage(null);

      if (!userId) {
        if (!isValidGuestEmail(guestEmail)) {
          setAddressValidationMessage('Please enter a valid email for order updates.');
          message.error('Please enter a valid email for order updates.');
          return false;
        }

        dispatch(setGuestShippingAddress({
          id: 'guest-shipping',
          ...createAddressData,
        } as IshipAddres));
        dispatch(resetLocation());
        setErrors({});

        if (shouldNavigate && location.pathname === '/cart/checkout') {
          navigate('/cart/checkout/payment');
        }

        return true;
      }

      if (isCreatingNewAddress || !shipping || !shipping.id) {
        await dispatch(createUserShip({ where: { id: userId }, data: createAddressData })).unwrap();
        message.success('Shipping address created!');
      } else {
        await dispatch(updateShippingAddress({ where: { id: shipping.id }, data: updatedData })).unwrap();
        message.success('Shipping address updated!');
      }

      dispatch(resetLocation());
      setErrors({});

      if (!isCreatingNewAddress && !shouldNavigate) {
        await dispatch(fetchUserShip({ selectedAddress, currentPage: '' })).unwrap();
      }

      if (shouldNavigate && location.pathname === '/cart/checkout') {
        navigate('/cart/checkout/payment');
      }

      return true;
    } catch {
      message.error('Failed to update shipping information.');
      return false;
    } finally {
      setUpdateButtonClicked(false);
      setLoading(false);
    }
  };

  return {
    handleUpdateProfile,
    loading,
    updateButtonClicked,
    addressValidationMessage,
    setAddressValidationMessage,
  };
};
